/** Client-side wrappers for the adaptive-only practice endpoints. */
import type { MathDiagram } from '@shared/diagrams/types';
import type { HelpRequestReasonCode } from '@shared/helpRequestReasons';
import type { MathSubject } from '@shared/mathSubjects';
import type { RecommendationReason } from '@shared/recommendation';
import type { RevisionSessionProjection } from '@shared/revisionSession';
import type { RevisionTopicsResponse } from '@shared/revisionTopics';
import type {
  AdaptiveQuestionPayload,
  ConfidenceSignal,
  SessionMode,
} from '@/components/Practice/adaptive/types';

import { API_URL, fetchWithConfig } from './apiUtils';

/**
 * Cohort the staff user is "trying out as". When set, the server reads it via
 * `parseRequestedStaffLevelStream(req.query)` and switches the session into
 * `practiceContext: 'staff_sim'` so attempts are tagged separately from the
 * staff user's own progress.
 */
export interface StaffLevelStream {
  orgLevel: string;
  examLevel: string;
  /** IP school sequence to order topics by; omitted for the base sequence. */
  cluster?: string | null;
  /**
   * E-Math / A-Math focus to preview; omitted for "Both", and meaningless
   * outside Sec 3/4 at G2/G3. The server re-derives it from the validated pair
   * rather than trusting it.
   */
  mathSubject?: MathSubject | null;
}

export interface PracticeCohortOptionsResponse {
  levelStreamOptions: StaffLevelStream[];
  selectedLevelStream: StaffLevelStream | null;
  allowAllLevelStreams: boolean;
}

export interface AdaptiveQuestionFlag {
  id: string;
  questionId: string;
  notes: string;
  helpReason?: HelpRequestReasonCode;
  flaggedAt: string;
  [key: string]: unknown;
}

interface AdaptiveFlagResponse {
  [key: string]: unknown;
}

const appendStaffLevelStream = (
  url: string,
  pair: StaffLevelStream | null | undefined
): string => {
  if (!pair) return url;
  const sep = url.includes('?') ? '&' : '?';
  const orgLevel = encodeURIComponent(pair.orgLevel);
  const examLevel = encodeURIComponent(pair.examLevel);
  const cluster = pair.cluster
    ? `&cluster=${encodeURIComponent(pair.cluster)}`
    : '';
  const mathSubject = pair.mathSubject
    ? `&mathSubject=${encodeURIComponent(pair.mathSubject)}`
    : '';
  return `${url}${sep}orgLevel=${orgLevel}&examLevel=${examLevel}${cluster}${mathSubject}`;
};

/**
 * Why the server refused to pin the node the student asked for. Mirrors
 * `PreferredDropReason` (`server/routes/practice/controllers/utils/
 * adaptiveSessionContext.ts`) — the session still starts, on a related node,
 * and this drives the "we couldn't pin that topic" toast.
 *
 * Declared once, as a const array, because every consumer needs BOTH the type
 * and a runtime membership check: `consumePreferredUnavailableToast` validates
 * a value read back out of `sessionStorage`, where it is untyped. This list
 * used to be spelled out in four separate unions plus one hand-rolled
 * three-way comparison, and the comparison is the dangerous copy — a reason
 * the server started sending but that list didn't know about was dropped to
 * `null`, silently swallowing the toast rather than failing loudly.
 */
export const PREFERRED_NODE_REASONS = [
  'unknown_node',
  'scope_mismatch',
  'not_deliverable',
  'subject_mismatch',
] as const;

export type PreferredNodeReason = (typeof PREFERRED_NODE_REASONS)[number];

export type SessionStatus = 'active' | 'ended' | 'expired';

export type SessionEndReason =
  | 'student_ended'
  | 'target_reached'
  | 'time_cap'
  | 'mastery_reached';

/**
 * Routing-algorithm intent stamped at session start (server-authoritative,
 * immutable). Distinct from `SessionMode` (drill | exam) which controls
 * feedback timing, not node-selection.
 *
 *  - `'pinned'`   — student picked a node in the topic DAG; routing stays
 *                   inside that node.
 *  - `'adaptive'` — Continue Practice / Daily; the posterior picker hops
 *                   between nodes weakest-mean first.
 */
export type PracticeMode = 'pinned' | 'adaptive';

export interface AdaptiveSessionMeta {
  sessionId: string;
  sessionMode: SessionMode;
  /**
   * Optional because sessions minted before the field shipped omit it. The
   * SessionTopRow mode chip's render gate is "show iff defined", so older
   * sessions correctly hide the chip rather than defaulting to a possibly
   * wrong value.
   */
  practiceMode?: PracticeMode;
  status: SessionStatus;
  targetNodeIds: string[];
  currentNodeId?: string | null;
  /** Human-readable strand for the session chip (e.g. "Algebra"). */
  currentStrand?: string | null;
  /** Human-readable node name for the session chip (e.g. "Simultaneous Linear Equations"). */
  currentNodeName?: string | null;
  /** Why the engine selected the current node; null for explicit pins. */
  currentNodeReason?: RecommendationReason | null;
  /**
   * Revision sessions only: the level-free chapter (and question type) the
   * round stays inside. The session chip reads it so the label holds across
   * a mixed round.
   */
  revision?: RevisionSessionProjection;
  /** 0-1 mastery fraction for the current node; exposed for session surfaces. */
  currentNodeMasteryPct?: number | null;
  /** Subject label (e.g. "E-Math") — populated on sessions post-subject-
   *  refactor; nullable for older sessions. */
  subject?: string | null;
  /** Topic label (e.g. "Algebra") — same population note as `subject`. */
  topic?: string | null;
  subtopic?: string | null;
  startedAt: string;
  endedAt: string | null;
  endReason: SessionEndReason | null;
  timeCapMs: number | null;
  /**
   * True only WHILE the cold-start placement diagnostic is running. Drives
   * the live suppression surface (no correct/wrong feedback, no chimes, no
   * valence, count-up progress). Flipped off server-side the moment the
   * stopping rule seeds the posterior profile; absent on ordinary sessions.
   */
  isDiagnostic?: boolean;
  /**
   * True when the session was MINTED as the placement diagnostic — survives
   * completion (unlike `isDiagnostic`). Drives the ended-session branches:
   * placement summary instead of the ordinary recap, "Initial diagnostic"
   * history labeling. Deliberately split from `isDiagnostic`: suppression
   * must end at the flip, labeling must persist forever.
   */
  wasDiagnostic?: boolean;
  /** Items answered so far in the live diagnostic (present iff isDiagnostic). */
  diagnosticItemsAnswered?: number;
}

export type SkillLevel = 'acquiring' | 'developing' | 'proficient' | 'mastered';

/**
 * Per-node mastery transition emitted by `buildSessionSummary` on the server.
 * Matches the wire shape produced by `server/routes/practice/utils/sessionSummary.ts`
 * (Lane A): entries only appear when the level actually moved, so the recap
 * only surfaces nodes that changed.
 *
 * Phase F: levels are the 4-state `SkillLevel`. The `needsReview` flag rides
 * alongside so the recap can keep its amber "Needs review" chip variant for
 * nodes the writer demoted to `flagged_gap`/`needs_teaching` mid-session —
 * the retired 6-state enum is no longer surfaced on the wire.
 */
export interface AdaptiveNodeMasteryDelta {
  nodeId: string;
  previousLevel: SkillLevel | null;
  newLevel: SkillLevel;
  becameMastered: boolean;
  regressed: boolean;
  needsReview: boolean;
  /** Skill display name, so the recap never shows a raw skill id. */
  nodeName?: string | null;
}

export interface AdaptiveSessionSummary {
  totalAttempts: number;
  correctCount: number;
  accuracy: number;
  misconceptionsFired: Array<{
    tag: string;
    count: number;
    label?: string;
    family?: string;
    familyLabel?: string;
    sampleMicroExplanation?: string | null;
  }>;
  nodeMastery: Array<{
    nodeId: string;
    level: SkillLevel;
    hasDueReview: boolean;
  }>;
  nodesMastered: number;
  nodesTargeted: number;
  /** Revision rounds: chapter skills at Proficient or above, out of the chapter. */
  proficientCount?: number;
  proficientOutOf?: number;
  /** Per-node mastery transitions observed this session (produced by Lane A). */
  masteryDeltas?: AdaptiveNodeMasteryDelta[];
  /** Post-close streak (server-authoritative; undefined until server lands it). */
  currentStreak?: number;
}

/**
 * Mirror of `IContent` on the server — the rendered text/latex payload we
 * persisted at submit time so we can replay the student's answer after the
 * 24h delivery-mapping TTL has expired. Null when the attempt predates the
 * schema migration or the mapping was otherwise lost.
 */
export interface AdaptiveContent {
  type: 'plain' | 'latex';
  text?: string;
  latex?: string;
  imageUrl?: string;
  imageAltText?: string;
}

/**
 * Per-attempt review row emitted by `buildSessionReview` on `/end`. The
 * server caps the list at 20 (see SESSION_REVIEW_ATTEMPT_CAP) and sets
 * `truncated: true` on the envelope when more attempts exist. Every content
 * field is nullable — either the attempt predates the write-side migration,
 * the question was deleted, or the tenant can't see the question any more.
 */
export interface AdaptiveSessionReviewAttempt {
  attemptId: string;
  questionId: string;
  nodeId: string;
  nodeName: string | null;
  questionContent: AdaptiveContent | null;
  isCorrect: boolean;
  answerContent: AdaptiveContent | null;
  correctAnswer: AdaptiveContent | null;
  solutionExplanation: AdaptiveContent | null;
  /**
   * Numbered solution-procedure steps captured at attempt time. Null on older
   * rows with no snapshot and on attempts whose author shipped no steps. The
   * recap UI hides the disclosure chip in both cases.
   */
  solutionSteps: AdaptiveContent[] | null;
  /**
   * Inline diagram snapshot captured at attempt time. Null on older rows with
   * no snapshot and on template attempts without `hasDiagram`. The recap UI
   * hides the diagram in both cases.
   */
  renderedDiagram: MathDiagram | null;
  /**
   * Solution-only diagram snapshot captured at attempt time. Null when the
   * worked solution had no diagram.
   */
  solutionDiagram: MathDiagram | null;
  misconceptionTag: string | null;
  misconceptionLabel: string | null;
  timeToAnswerMs: number | null;
  /**
   * True when the student already has a help-request thread keyed to this
   * `(questionId, attemptId)` — including flags raised live in-session. The
   * review surfaces show the quiet "Help requested" state instead of the
   * "Ask for help" affordance. Optional so cached recaps and the
   * `practiceSessionContext` shape stay type-compatible; absent ⇒ not asked.
   */
  helpRequested?: boolean;
}

/**
 * Post-close streak snapshot from `bumpStreakOnCompletion` /
 * `readStreakSnapshot`. `bumpedThisSession` is true only on the transition
 * that actually moved the counter; idempotent re-calls of `/end` return
 * `false` so the recap knows whether to pulse the pill.
 */
export interface AdaptiveStreakInfo {
  currentStreak: number;
  lastPracticeDate: string | null;
  bumpedThisSession: boolean;
}

/**
 * One node of the frozen placement profile captured when the diagnostic
 * sealed (θ̂ → posterior seeding). `level` is the seeded starting level —
 * capped below `mastered` by construction, so the placement buckets are
 * proficient / developing / acquiring.
 */
export interface DiagnosticPlacementNode {
  nodeId: string;
  nodeName: string | null;
  /** Syllabus topic grouping; null when the node isn't linked to a topic. */
  topicName: string | null;
  level: SkillLevel;
  /**
   * Whether the node is still prereq-locked given the placement levels.
   * Derived server-side at read time (frozen levels + live KG prereqs).
   * Optional: payloads cached before this field shipped lack it — the
   * "Skills unlocked" stat falls back to "Skills mapped" in that case.
   */
  locked?: boolean;
}

/**
 * Placement block returned on `/end` and `/recap` for sessions minted as the
 * cold-start diagnostic. Server truth, frozen at completion — re-reading it
 * weeks later shows where the student STARTED, not where they are now.
 */
export interface DiagnosticPlacement {
  completedAt: string | null;
  itemsAnswered: number;
  nodes: DiagnosticPlacementNode[];
}

export interface AdaptiveSessionEndResponse {
  session: AdaptiveSessionMeta;
  summary: AdaptiveSessionSummary;
  attempts: AdaptiveSessionReviewAttempt[];
  truncated: boolean;
  streak: AdaptiveStreakInfo;
  /** Present iff the session was minted as the placement diagnostic. */
  placement?: DiagnosticPlacement;
}

export interface AdaptiveQuestionResponse {
  question: AdaptiveQuestionPayload;
  sessionId: string;
  resumed: boolean;
  /**
   * Node the question was served from, echoed per-question because adaptive
   * sessions advance `currentNodeId` server-side at serve time. Lets the
   * in-session header chip track node hops without refetching session meta.
   * Optional on the wire: older servers omit both fields.
   */
  currentNodeId?: string | null;
  currentNodeName?: string | null;
  currentNodeReason?: RecommendationReason | null;
}

/**
 * Per-wrong-answer feedback authored at question import time. Three
 * plain-text fields (KaTeX `$...$` allowed). The renderer shows all three
 * as a labelled sequence: "Common mistake", "Why it's wrong", "Correct move".
 */
export interface AdaptiveMicroExplanation {
  nameTheError: string;
  showWhyWrong: string;
  showCorrectMove: string;
}

/**
 * Author-supplied solution explanation surfaced after a correct submit in
 * drill mode. Mirrors the server's `IContent` shape so the renderer can pass
 * it straight to MathContent. Absent in exam mode or when the question has
 * no authored solution.
 */
export interface AdaptiveSolutionContent {
  type: 'plain' | 'latex';
  text?: string;
  latex?: string;
}

export interface AdaptiveAttemptResponse {
  correct: boolean;
  answer?: unknown;
  /**
   * Immutable `{ content, steps }` envelope from the adaptive delivery.
   * `content` is the explanation and `steps` is the per-attempt numbered
   * procedure snapshot (possibly empty when the author shipped no steps).
   */
  solution?: {
    content: AdaptiveSolutionContent;
    steps?: AdaptiveSolutionContent[];
    diagram?: MathDiagram | null;
  } | null;
  /** Convenience alias for `solution.content`; same shape. */
  explanation?: AdaptiveSolutionContent | null;
  misconceptionTagFired: string | null;
  errorTypeFired: string | null;
  /**
   * Structured per-wrong-answer feedback. Server returns null for correct
   * picks or exam-mode adaptive deliveries.
   */
  microExplanation?: AdaptiveMicroExplanation | null;
  /**
   * MCQ key the server considers correct. Present on drill-mode adaptive
   * responses so the UI can paint the correct option in green alongside the
   * chosen-wrong row in brick. Absent for exam responses.
   */
  correctOptionKey?: string | null;
  /**
   * Set on the attempt that crosses the daily-target boundary (stackable
   * bites). `true` when the server actually advanced the StudentStreak this
   * attempt; `false` when the boundary latch held but the bump no-oped
   * (e.g. same-day repeat session — `lastPracticeDate` already today).
   * Absent on every other attempt. The bite-celebration overlay reads this
   * to decide whether to render the "+1" flourish honestly.
   */
  streakBumped?: boolean;
  /**
   * Authoritative post-bump streak counter, present alongside
   * `streakBumped` on the boundary attempt. The overlay must render this
   * value (not `cached preview + 1`): a comeback session after a lapse
   * RESETS the counter to 1, while the cached daily-preview value is still
   * the stale pre-lapse streak.
   */
  streakCurrent?: number;
  /**
   * Present (true) when this attempt completed the cold-start placement
   * diagnostic — either it fired the engine's stopping rule or the session
   * had already sealed server-side. The client ends the session on this
   * signal and shows the placement summary. Idempotent across replays
   * (keyed on the delivery's durable routeKind, not transient state).
   */
  diagnosticComplete?: boolean;
  /**
   * True when the server's rapid-guess guard judged this answer sub-floor.
   * Server-authoritative and NOT re-derivable client-side: the verdict depends
   * on timing fallbacks, anomaly handling, and (on a retry) the value frozen
   * on the original history row. The client only counts consecutive flags to
   * decide when to show the "take your time" nudge. Absent on exam or
   * non-durable responses.
   */
  rapidGuessFlagged?: boolean;
}

/**
 * Every adaptive endpoint is student-owned and tenant-scoped, so each method
 * accepts a required `authToken` (Auth0 ID token). The token is attached via
 * `fetchWithConfig`'s `authToken` option so the `Authorization: Bearer ...`
 * header is never forgotten. Callers should read the token from the auth
 * context (e.g. `useAuth().getAccessToken()`).
 */
export const adaptivePracticeApi = {
  /**
   * The student's Revision section: earlier-year chapters of their own stream.
   * Same focus/staff params as `/practice/topic-overview`.
   */
  getRevisionTopics: (
    opts: {
      mathSubject?: string | null;
      staffOrgLevel?: string | null;
      staffExamLevel?: string | null;
      staffCluster?: string | null;
    },
    authToken: string
  ) => {
    const params = new URLSearchParams();
    if (opts.mathSubject) params.set('mathSubject', opts.mathSubject);
    if (opts.staffOrgLevel && opts.staffExamLevel) {
      params.set('orgLevel', opts.staffOrgLevel);
      params.set('examLevel', opts.staffExamLevel);
      if (opts.staffCluster) params.set('cluster', opts.staffCluster);
    }
    const qs = params.toString();
    return fetchWithConfig<RevisionTopicsResponse>(
      `/practice/revision-topics${qs ? `?${qs}` : ''}`,
      { authToken }
    );
  },

  getCohortOptions: (authToken: string) =>
    fetchWithConfig<PracticeCohortOptionsResponse>('/practice/cohort-options', {
      authToken,
    }),

  getAdaptiveQuestionFlags: async (
    userId: string,
    questionId: string,
    adaptiveAttemptId: string,
    authToken: string
  ): Promise<AdaptiveQuestionFlag[]> => {
    const query = new URLSearchParams({ adaptiveAttemptId });
    const response = await fetchWithConfig<AdaptiveQuestionFlag[]>(
      `/practice/flags/${userId}/${questionId}?${query.toString()}`,
      { authToken }
    );
    return response.map(flag => ({
      ...flag,
      flaggedAt: new Date(flag.flaggedAt).toISOString(),
    }));
  },

  flagAdaptiveQuestion: (
    userId: string,
    subject: string,
    questionId: string,
    notes: string,
    topic: string | undefined,
    subtopic: string | undefined,
    adaptiveAttemptId: string,
    authToken: string,
    nodeId?: string,
    flagId?: string,
    helpReason?: HelpRequestReasonCode
  ): Promise<AdaptiveFlagResponse> =>
    fetchWithConfig<AdaptiveFlagResponse>('/practice/flag-question', {
      method: 'POST',
      body: JSON.stringify({
        userId,
        subject,
        questionId,
        notes,
        ...(topic ? { topic } : {}),
        ...(subtopic ? { subtopic } : {}),
        adaptiveAttemptId,
        ...(nodeId ? { nodeId } : {}),
        ...(flagId ? { flagId } : {}),
        ...(helpReason ? { helpReason } : {}),
      }),
      authToken,
    }),

  updateAdaptiveFlag: (
    userId: string,
    subject: string,
    questionId: string,
    notes: string,
    adaptiveAttemptId: string,
    authToken: string,
    helpReason?: HelpRequestReasonCode
  ): Promise<AdaptiveFlagResponse> =>
    fetchWithConfig<AdaptiveFlagResponse>('/practice/flag-question', {
      method: 'PATCH',
      body: JSON.stringify({
        userId,
        subject,
        questionId,
        notes,
        adaptiveAttemptId,
        ...(helpReason ? { helpReason } : {}),
      }),
      authToken,
    }),

  updateAdaptiveFlagHelpReason: (
    userId: string,
    subject: string,
    questionId: string,
    adaptiveAttemptId: string,
    helpReason: HelpRequestReasonCode,
    authToken: string
  ): Promise<AdaptiveFlagResponse> =>
    fetchWithConfig<AdaptiveFlagResponse>('/practice/flag-question', {
      method: 'PATCH',
      body: JSON.stringify({
        userId,
        subject,
        questionId,
        adaptiveAttemptId,
        helpReason,
      }),
      authToken,
    }),

  startSession: (
    opts: {
      sessionMode?: SessionMode;
      subject?: string;
      topic?: string;
      subtopic?: string;
      preferredStartNodeId?: string;
      /**
       * A Revision topic id: the session stays inside that earlier-year
       * chapter. Sent only from revision pages.
       */
      preferredStartTopicId?: string;
      /** Exam-layout question type id inside that chapter (Sec 4 G1/G2/G3). */
      preferredStartQuestionTypeId?: string;
      timeCapMs?: number;
      /**
       * Staff-sim cohort: serialised as `?orgLevel=…&examLevel=…` on the
       * request URL because the server reads via `req.query`, even on POST.
       */
      staffLevelStream?: StaffLevelStream | null;
    },
    authToken: string
  ) => {
    const { staffLevelStream, ...body } = opts;
    return fetchWithConfig<{
      session: AdaptiveSessionMeta;
      resumed: boolean;
      /**
       * Present only when `resumed: true` — carries the server-authoritative
       * running totals so the client can seed `liveProgress` instead of
       * restarting the mid-session target-reached gate from zero.
       */
      summary?: AdaptiveSessionSummary;
      /**
       * True when the resume path repointed the existing session onto a
       * client-supplied `preferredStartNodeId` that differed from the
       * session's `currentNodeId`. Always false on a fresh mint or a plain
       * resume without a preferred node. Drives the "Now practising …" toast.
       */
      repointed?: boolean;
      /**
       * Present only when `preferredStartNodeId` was sent. False when the
       * server dropped the pin (unknown id, scope mismatch, or no deliverable
       * question); true when the pin was honored (fresh-mint head, repoint, or
       * already-on-it).
       */
      preferredNodeAvailable?: boolean;
      /**
       * Reason code for the silent-drop. Set only when `preferredNodeAvailable`
       * is `false`. Drives the "we couldn't pin that topic" toast.
       */
      preferredNodeReason?: PreferredNodeReason;
    }>(
      appendStaffLevelStream(
        '/practice/session/adaptive/start',
        staffLevelStream
      ),
      {
        method: 'POST',
        body: JSON.stringify(body),
        authToken,
      }
    );
  },

  /**
   * Start a Continue Practice (free-session) drill. Pulls from the full set
   * of in-scope, non-mastered nodes for the student instead of the daily 5.
   * Returns `{ empty: true, reason }` when the student has nothing left to
   * practise — caller should render a celebratory state, not navigate.
   */
  startFreeSession: (
    opts:
      | {
          subject?: string;
          topic?: string;
          subtopic?: string;
          staffLevelStream?: StaffLevelStream | null;
        }
      | undefined,
    authToken: string
  ) => {
    const { staffLevelStream, ...body } = opts ?? {};
    return fetchWithConfig<
      | {
          empty?: false;
          session: AdaptiveSessionMeta;
          resumed: boolean;
          summary?: AdaptiveSessionSummary;
        }
      | {
          empty: true;
          reason: 'all_mastered' | 'no_in_scope_nodes';
          session: null;
        }
    >(
      appendStaffLevelStream(
        '/practice/session/adaptive/free-start',
        staffLevelStream
      ),
      {
        method: 'POST',
        body: JSON.stringify(body),
        authToken,
      }
    );
  },

  getSessionState: (sessionId: string, authToken: string) =>
    fetchWithConfig<{
      session: AdaptiveSessionMeta;
      summary: AdaptiveSessionSummary;
    }>(`/practice/session/adaptive/${encodeURIComponent(sessionId)}/state`, {
      authToken,
    }),

  endSession: (
    sessionId: string,
    authToken: string,
    endReason?: SessionEndReason
  ) =>
    fetchWithConfig<AdaptiveSessionEndResponse>(
      `/practice/session/adaptive/${encodeURIComponent(sessionId)}/end`,
      {
        method: 'POST',
        body: JSON.stringify(endReason ? { endReason } : {}),
        authToken,
      }
    ),

  /**
   * Append the next DAILY_TARGET weakest-posterior nodes onto an open
   * session. Fired when the student taps "Keep going +5" on the bite
   * overlay. The server dedupes against the existing `targetNodeIds` and
   * appends up to DAILY_TARGET fresh nodes.
   *
   * 409 error codes the caller branches on (carried on the rejected error
   * via `fetchWithConfig`'s `isClientError` envelope):
   *  - `EXTEND_EXHAUSTED`     — no new in-scope nodes; offer Done.
   *  - `SESSION_IDLE_EXPIRED` — idle > 4h; route to a fresh session.
   *  - `SESSION_NOT_EXTENDABLE` — session ended / wrong mode.
   */
  extendSession: (sessionId: string, authToken: string) =>
    fetchWithConfig<{
      session: AdaptiveSessionMeta;
      summary: AdaptiveSessionSummary;
      appendedNodeIds: string[];
    }>(`/practice/session/adaptive/${encodeURIComponent(sessionId)}/extend`, {
      method: 'POST',
      body: JSON.stringify({}),
      authToken,
    }),

  /**
   * Fire-and-forget session close from `beforeunload`. Uses
   * `navigator.sendBeacon` so the request survives page tear-down (regular
   * fetch is cancelled when the tab closes). The bearer token can't ride on
   * a header — beacon doesn't support custom headers — so it goes in the
   * JSON body and the server shims it onto Authorization before auth runs.
   * text/plain content-type is CORS-safelisted, no preflight, so the request
   * actually goes out before the unload completes.
   *
   * Returns `true` when the browser queued the beacon, `false` when it
   * couldn't (over the per-origin beacon size budget, or `sendBeacon`
   * unavailable). Caller does not await — this is best-effort.
   */
  endSessionBeacon: (
    sessionId: string,
    authToken: string,
    endReason: SessionEndReason = 'student_ended'
  ): boolean => {
    if (
      typeof navigator === 'undefined' ||
      typeof navigator.sendBeacon !== 'function'
    ) {
      return false;
    }
    const url = `${API_URL}/practice/session/adaptive/${encodeURIComponent(
      sessionId
    )}/end-beacon`;
    const payload = JSON.stringify({ idToken: authToken, endReason });
    const blob = new Blob([payload], { type: 'text/plain' });
    try {
      return navigator.sendBeacon(url, blob);
    } catch {
      return false;
    }
  },

  fetchNextQuestion: (
    userId: string,
    subject: string,
    authToken: string,
    topic?: string,
    subtopic?: string,
    staffLevelStream?: StaffLevelStream | null,
    /** The tab's session; a closed one answers 409 `session_closed`. */
    sessionId?: string | null
  ) => {
    let endpoint = `/practice/questions/${encodeURIComponent(
      userId
    )}/${encodeURIComponent(subject)}`;
    if (topic && topic !== 'All Topics') {
      endpoint += `/${encodeURIComponent(topic)}`;
      if (subtopic && subtopic !== 'All Subtopics') {
        endpoint += `/${encodeURIComponent(subtopic)}`;
      }
    }
    const withLevel = appendStaffLevelStream(endpoint, staffLevelStream);
    const url = sessionId
      ? `${withLevel}${withLevel.includes('?') ? '&' : '?'}sessionId=${encodeURIComponent(sessionId)}`
      : withLevel;
    return fetchWithConfig<AdaptiveQuestionResponse>(url, { authToken });
  },

  submitAttempt: (
    payload: {
      userId: string;
      subject: string;
      questionId: string;
      attemptId: string;
      chosenKey: string;
      duration: number;
      topic?: string;
      subtopic?: string;
      sessionId: string;
      confidenceSignal?: ConfidenceSignal;
      sessionMode?: SessionMode;
      timeToAnswerMs?: number;
    },
    authToken: string
  ) =>
    fetchWithConfig<AdaptiveAttemptResponse>('/practice/attempt', {
      method: 'POST',
      body: JSON.stringify({
        userId: payload.userId,
        subject: payload.subject,
        questionId: payload.questionId,
        answer: payload.chosenKey,
        duration: payload.duration,
        correct: false, // authoritative value comes from server
        isGenerated: false,
        topic: payload.topic ?? '',
        subtopic: payload.subtopic ?? '',
        sessionId: payload.sessionId,
        attemptId: payload.attemptId,
        chosenKey: payload.chosenKey,
        confidenceSignal: payload.confidenceSignal,
        sessionMode: payload.sessionMode,
        timeToAnswerMs: payload.timeToAnswerMs,
      }),
      authToken,
    }),
};
